import {put, select, take, fork, call} from 'redux-saga/effects';
import {efServiceAssignmentTypes} from '@ace-de/eua-entity-types';
import fetchRequest from '../../application/sagas/fetchRequest';
import * as serviceCaseHelpers from '../../service-cases/sagas/serviceCaseHelpers';
import updateServiceAssignment from './updateServiceAssignment';
import * as serviceAssignmentActionTypes from '../serviceAssignmentActionTypes';
import getMapExtentFromCenter from '../../utils/getMapExtentFromCenter';

const searchServiceAssignmentTowingDestinations = function* searchServiceAssignmentTowingDestinations({payload}) {
    const {serviceCaseId, serviceAssignmentLineNo, searchQuery} = payload;

    const {serviceCases} = yield select(state => state.serviceCases);
    const {serviceAssignments} = yield select(state => state.serviceAssignments);
    const serviceCase = serviceCases[serviceCaseId];
    const serviceAssignmentId = `${serviceCaseId}-${serviceAssignmentLineNo}`;
    const serviceAssignment = serviceAssignments[serviceAssignmentId];
    if (!serviceAssignment || !serviceCase.damage || !serviceCase.damage.location) return;

    yield put({
        type: serviceAssignmentActionTypes.SET_TOWING_DESTINATION_RECOMMENDATIONS,
        payload: {serviceAssignmentId, towingDestinationRecommendationDTOs: []},
    });

    const {serviceManager} = yield select(state => state.application);
    const googleMapsService = serviceManager.loadService('googleMapsService');

    yield* serviceCaseHelpers.setPersistencePending(serviceCaseId);

    const damageLocation = serviceCase.damage.location;

    const mapExtent = getMapExtentFromCenter(
        damageLocation.latitude,
        damageLocation.longitude,
        11,
        window.innerWidth,
        window.innerHeight,
    );

    const {longestDistance} = yield call(googleMapsService.getLongestDistanceFromPoint, damageLocation, mapExtent);
    const mapViewport = {
        southwest: {
            longitude: mapExtent.xmin,
            latitude: mapExtent.ymin,
        },
        northeast: {
            longitude: mapExtent.xmax,
            latitude: mapExtent.ymax,
        },
    };

    yield fork(
        fetchRequest,
        serviceAssignmentActionTypes.SEARCH_TOWING_DESTINATION_GOOGLE_PLACES_REQUEST,
        googleMapsService.searchPlaces,
        {
            searchQuery: searchQuery,
            longitude: damageLocation.longitude,
            latitude: damageLocation.latitude,
            locationRadius: Math.floor(longestDistance) * 1000 || null,
            viewport: mapViewport
                ? {
                    southwest: mapViewport.southwest,
                    northeast: mapViewport.northeast,
                }
                : null,
        },
    );

    const placesSearchResponseAction = yield take([
        serviceAssignmentActionTypes.SEARCH_TOWING_DESTINATION_GOOGLE_PLACES_REQUEST_FAILED,
        serviceAssignmentActionTypes.SEARCH_TOWING_DESTINATION_GOOGLE_PLACES_REQUEST_SUCCEEDED,
    ]);

    if (placesSearchResponseAction.error) {
        yield* serviceCaseHelpers.setPersistenceReady(serviceCaseId);
        return;
    }

    const {response: placesSearchResponse} = placesSearchResponseAction.payload;
    const {googlePlaceDTOs} = placesSearchResponse;

    yield fork(
        fetchRequest,
        serviceAssignmentActionTypes.FETCH_TOWING_DESTINATION_GEOLOCATIONS_REQUEST,
        googleMapsService.sortDistances,
        {
            origin: {
                location: {
                    latLng: {
                        latitude: damageLocation.latitude,
                        longitude: damageLocation.longitude,
                    },
                },
            },
            destinations: googlePlaceDTOs.map(googlePlaceDTO => ({
                apiUnrelatedFields: googlePlaceDTO,
                location: {
                    latLng: {
                        latitude: googlePlaceDTO.latitude,
                        longitude: googlePlaceDTO.longitude,
                    },
                },
            })),
        },
    );

    const fetchGeolocationsResponseAction = yield take([
        serviceAssignmentActionTypes.FETCH_TOWING_DESTINATION_GEOLOCATIONS_REQUEST_FAILED,
        serviceAssignmentActionTypes.FETCH_TOWING_DESTINATION_GEOLOCATIONS_REQUEST_SUCCEEDED,
    ]);

    if (fetchGeolocationsResponseAction.error) {
        yield* serviceCaseHelpers.setPersistenceReady(serviceCaseId);
        return;
    }

    const {response: fetchGeolocationsResponse} = fetchGeolocationsResponseAction.payload;
    const {distances} = fetchGeolocationsResponse;

    const towingDestinationRecommendationDTOs = distances.map(distance => ({
        ...distance.destination.apiUnrelatedFields,
        routeToReferentialPoint: {
            totalKilometers: distance.distanceMeters / 1000,
            totalTravelTime: Number(distance.duration.replace('s', '')) / 60,
        },
    }));

    // Determine if selected towing destination is within suggestion results
    let selectedLocationFromSuggestions = null;
    if (serviceAssignment.towingDestination?.location) {
        const {id, locationName, latitude, longitude} = serviceAssignment.towingDestination.location;

        yield fork(
            fetchRequest,
            serviceAssignmentActionTypes.FETCH_DAMAGE_LOCATION_TO_TOWING_DESTINATION_ROUTE_REQUEST,
            googleMapsService.getRoute,
            {
                origin: {
                    location: {
                        latLng: {
                            latitude: damageLocation.latitude,
                            longitude: damageLocation.longitude,
                        },
                    },
                },
                destination: {
                    location: {
                        latLng: {
                            latitude,
                            longitude,
                        },
                    },
                },
                fields: ['routes.distanceMeters', 'routes.duration', 'routes.polyline.encodedPolyline'],
            },
        );
        const routeResponseAction = yield take([
            serviceAssignmentActionTypes.FETCH_DAMAGE_LOCATION_TO_TOWING_DESTINATION_ROUTE_REQUEST_FAILED,
            serviceAssignmentActionTypes.FETCH_DAMAGE_LOCATION_TO_TOWING_DESTINATION_ROUTE_REQUEST_SUCCEEDED,
        ]);
        let routeToReferentialPoint = null;

        if (!routeResponseAction.error) {
            const {response} = routeResponseAction.payload;
            const {routes} = response;
            routeToReferentialPoint = routes[0];
        }

        const selectedLocationFromSuggestionsIndex = towingDestinationRecommendationDTOs
            .findIndex(towingDestinationDTO => {
                const placesIdBase = towingDestinationDTO['placeId'].split('--')[0];
                const locationIdExtent = towingDestinationDTO['locationId'].split('_')[1];

                return (towingDestinationDTO['locationId'] === id
                    || (towingDestinationDTO['locationName'] === locationName
                        && (placesIdBase === id || `${placesIdBase}_${locationIdExtent}` === id)
                    )
                );
            });

        selectedLocationFromSuggestions = towingDestinationRecommendationDTOs[selectedLocationFromSuggestionsIndex];

        if (!selectedLocationFromSuggestions) {
            towingDestinationRecommendationDTOs.unshift({
                ...serviceAssignment.towingDestination.location.toDTO(),
                ...(routeToReferentialPoint && {
                    routeToReferentialPoint: {
                        totalKilometers: routeToReferentialPoint.distanceMeters / 1000,
                        totalTravelTime: Number(routeToReferentialPoint.duration.replace('s', '')) / 60,
                        encodedPolyline: routeToReferentialPoint.polyline.encodedPolyline,
                    },
                }),
            });
        } else if (routeToReferentialPoint) {
            towingDestinationRecommendationDTOs.splice(selectedLocationFromSuggestionsIndex, 1, {
                ...selectedLocationFromSuggestions,
                routeToReferentialPoint: {
                    totalKilometers: routeToReferentialPoint.distanceMeters / 1000,
                    totalTravelTime: Number(routeToReferentialPoint.duration.replace('s', '')) / 60,
                    encodedPolyline: routeToReferentialPoint.polyline.encodedPolyline,
                },
            });
        }

        if (selectedLocationFromSuggestions && selectedLocationFromSuggestions.locationId !== id) {
            yield* updateServiceAssignment({
                caller: serviceAssignmentActionTypes.SUBMIT_TOWING_DESTINATION_FORM,
                assignmentType: efServiceAssignmentTypes.VEHICLE,
                serviceAssignmentLineNo,
                serviceCaseId,
                serviceAssignmentData: {
                    towingDestination: {
                        ...serviceAssignment.towingDestination?.location,
                        locationId: selectedLocationFromSuggestions.locationId,
                    },
                },
            });
        }
    }

    const recommendedTowingDestinationDTO = towingDestinationRecommendationDTOs[0];

    // render routes for towing destination options
    if (recommendedTowingDestinationDTO && !selectedLocationFromSuggestions) {
        yield fork(
            fetchRequest,
            serviceAssignmentActionTypes.FETCH_DAMAGE_LOCATION_TO_TOWING_DESTINATION_ROUTE_REQUEST,
            googleMapsService.getRoute,
            {
                origin: {
                    location: {
                        latLng: {
                            latitude: damageLocation.latitude,
                            longitude: damageLocation.longitude,
                        },
                    },
                },
                destination: {
                    location: {
                        latLng: {
                            latitude: recommendedTowingDestinationDTO.latitude,
                            longitude: recommendedTowingDestinationDTO.longitude,
                        },
                    },
                },
                fields: ['routes.distanceMeters', 'routes.duration', 'routes.polyline.encodedPolyline'],
            },
        );
        const routeResponseAction = yield take([
            serviceAssignmentActionTypes.FETCH_DAMAGE_LOCATION_TO_TOWING_DESTINATION_ROUTE_REQUEST_FAILED,
            serviceAssignmentActionTypes.FETCH_DAMAGE_LOCATION_TO_TOWING_DESTINATION_ROUTE_REQUEST_SUCCEEDED,
        ]);

        if (!routeResponseAction.error) {
            const {response} = routeResponseAction.payload;
            const {routes} = response;
            const routeToReferentialPoint = routes[0];

            if (routeToReferentialPoint) {
                towingDestinationRecommendationDTOs[0].routeToReferentialPoint = {
                    totalKilometers: routeToReferentialPoint.distanceMeters / 1000,
                    totalTravelTime: Number(routeToReferentialPoint.duration.replace('s', '')) / 60,
                    encodedPolyline: routeToReferentialPoint.polyline.encodedPolyline,
                };
            }
        }
    }

    yield put({
        type: serviceAssignmentActionTypes.SET_TOWING_DESTINATION_RECOMMENDATIONS,
        payload: {
            serviceAssignmentId,
            towingDestinationRecommendationDTOs,
            recommendedTowingDestinationId: recommendedTowingDestinationDTO
                ? recommendedTowingDestinationDTO['locationId'] : null,
        },
    });

    yield* serviceCaseHelpers.setPersistenceReady(serviceCaseId);
};

export default searchServiceAssignmentTowingDestinations;
